'use client';

import { useRouter, usePathname, useSearchParams, useParams } from 'next/navigation';
import { useIntl } from 'react-intl';
import { useIsBot } from '@packages/tracking/src/hooks/useIsBot/useIsBot';
import { type FragmentType } from '@packages/gql/generated/shopping';
import type { CustomerReviewTagChipFragmentFragmentDoc } from '@packages/gql/generated/shopping/CustomerReviewTagChipFragmentFragmentDoc';
import type { Product } from '@packages/gql/generated/shopping/graphql';
import { unmask } from '@packages/gql/src/betterMasking';
import { buildUrl } from '@packages/shared/src/utils/buildUrl/buildUrl';
import { Chip } from '@packages/shared/src/ui/Chip/Chip';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { ratingDetailPagePath, ratingFilterTagPrefix } from '../../CustomerRatings/config';
import type { CustomerRatingsParams } from '../../CustomerRatingsAndReviewsPage/types';
import type { CustomerReviewTagChipCategory } from '../Tracking/useCustomerReviewsTracking/useCustomerReviewsTracking';
import { useCustomerReviewsTracking } from '../Tracking/useCustomerReviewsTracking/useCustomerReviewsTracking';

/* GraphQL */ `
  fragment CustomerReviewTagChipFragment on RatingTag {
    ratingCount
    tag {
      id
      key
      name
    }
  }
`;

export type CustomerReviewTagChipProps = {
  data: FragmentType<typeof CustomerReviewTagChipFragmentFragmentDoc>;
  /**
   * sku needed for product in rating page
   */
  sku: Product['sku'];
  /**
   * category for glycerin tracking
   */
  category: CustomerReviewTagChipCategory;
};

export const CustomerReviewTagChip = ({ data, sku, category }: CustomerReviewTagChipProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams<CustomerRatingsParams>();
  const { push } = useRouter();
  const isBot = useIsBot();
  const { formatMessage } = useIntl();
  const { localizeUrl, language } = useI18n();

  const { trackCustomerReviewTagChipClick } = useCustomerReviewsTracking();

  const {
    ratingCount,
    tag: { key, name },
  } = unmask<typeof CustomerReviewTagChipFragmentFragmentDoc>(data);

  const filterTag = `${ratingFilterTagPrefix}${key}`;
  const ftag = params?.ftag ? decodeURIComponent(params.ftag[0]) : undefined;
  const isActive = ftag === filterTag;

  const onClickHandler = () => {
    trackCustomerReviewTagChipClick(category, name);

    const searchParamsWithSKU = new URLSearchParams(searchParams?.toString());
    searchParamsWithSKU.set('sku', sku);
    push(
      localizeUrl(
        buildUrl({
          pathname: `/p/${params!.akl ?? params!.slug}/rating${isActive ? '' : `/${filterTag}`}`,
          searchParams: searchParamsWithSKU,
        }),
        language,
      ),
      { scroll: !pathname!.includes(ratingDetailPagePath) },
    );
  };

  return (
    <Chip
      aria-label={formatMessage(
        {
          id: 'rating.tag.chip.label',
          defaultMessage: '{name} {ratingCount} mal erwähnt',
        },
        { name, ratingCount },
      )}
      key={key}
      label={`${name} (${ratingCount})`}
      variant="filled"
      selected={isActive}
      {...(key &&
        !isBot &&
        pathname !== null &&
        params !== null && {
          onClick: onClickHandler,
        })}
    />
  );
};
